import { useState, useMemo } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { getQueryFn, apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { useLocation, useRoute, useSearch } from "wouter";
import { useIsMobile } from "@/hooks/use-mobile";
import {
  ResizablePanelGroup,
  ResizablePanel,
  ResizableHandle,
} from "@/components/ui/resizable";
import { TicketListItem } from "@/components/admin/support/ticket-list-item";
import { TicketDetailPanel } from "@/components/admin/support/ticket-detail-panel";
import {
  TicketForm,
  type TicketFormValues,
} from "@/components/admin/support/ticket-form";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Plus, Ticket, ArrowLeft } from "lucide-react";
import type { Ticket as TicketType } from "@db/schema";

const ALL_TICKETS_EXCLUDED_STATUSES = new Set(["closed", "spam", "deleted"]);

export default function TicketsPage() {
  const { toast } = useToast();
  const [, navigate] = useLocation();
  const currentSearch = useSearch();
  const isMobile = useIsMobile();

  // Route params: /tickets/:id? — id is optional
  const [, params] = useRoute("/tickets/:id");
  const selectedId = params?.id;

  // Read status filter from URL search params
  const statusFilter = useMemo(
    () => new URLSearchParams(currentSearch).get("status") ?? "all",
    [currentSearch],
  );
  const normalizedSearch = useMemo(() => {
    const params = new URLSearchParams(currentSearch);
    const query = params.toString();
    return query ? `?${query}` : "";
  }, [currentSearch]);
  const ticketsListHref = `/tickets${normalizedSearch}`;

  const [search, setSearch] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);

  const { data: tickets, isLoading } = useQuery<TicketType[]>({
    queryKey: ["/api/support/tickets"],
    queryFn: getQueryFn({ on401: "throw" }),
    refetchInterval: 30_000,
  });

  const filteredTickets = useMemo(() => {
    if (!tickets) return [];
    let result = tickets;

    if (statusFilter === "all") {
      result = result.filter((t) => !ALL_TICKETS_EXCLUDED_STATUSES.has(t.status));
    } else {
      result = result.filter((t) => t.status === statusFilter);
    }
    if (search.trim()) {
      const term = search.toLowerCase();
      result = result.filter(
        (t) =>
          t.key?.toLowerCase().includes(term) ||
          t.subject?.toLowerCase().includes(term),
      );
    }

    return result;
  }, [tickets, statusFilter, search]);

  const createMutation = useMutation({
    mutationFn: async (data: TicketFormValues) => {
      const { description, ...ticketData } = data;
      const res = await apiRequest("POST", "/api/support/tickets", ticketData);
      const ticket = (await res.json()) as TicketType;

      if (description?.trim()) {
        await apiRequest("POST", `/api/support/tickets/${ticket.id}/reply`, {
          htmlBody: description,
          to: "",
        });
      }

      return ticket;
    },
    onSuccess: (ticket) => {
      queryClient.invalidateQueries({ queryKey: ["/api/support/tickets"] });
      queryClient.invalidateQueries({ queryKey: ["/api/support/tickets/counts"] });
      setDialogOpen(false);
      toast({ title: "Ticket created successfully" });
      navigate(`/tickets/${ticket.id}${normalizedSearch}`);
    },
    onError: (err: Error) => {
      toast({
        title: "Failed to create ticket",
        description: err.message,
        variant: "destructive",
      });
    },
  });

  // Mobile: show detail only when a ticket is selected
  if (isMobile && selectedId) {
    return (
      <div className="flex flex-col h-[calc(100dvh-3.5rem)]">
        <div className="flex items-center gap-2 px-4 py-2 border-b">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate(ticketsListHref)}
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <span className="text-sm font-medium">Back to tickets</span>
        </div>
        <div className="flex-1 overflow-hidden">
          <TicketDetailPanel ticketId={selectedId} />
        </div>
      </div>
    );
  }

  // Mobile: show list only
  if (isMobile) {
    return (
      <div className="flex flex-col h-[calc(100dvh-3.5rem)]">
        <ListPanel
          tickets={filteredTickets}
          isLoading={isLoading}
          selectedId={selectedId}
          search={search}
          onSearchChange={setSearch}
          onSelect={(id) => navigate(`/tickets/${id}${normalizedSearch}`)}
          onCreateClick={() => setDialogOpen(true)}
        />
        <CreateDialog
          open={dialogOpen}
          onOpenChange={setDialogOpen}
          onSubmit={(data) => createMutation.mutate(data)}
          isPending={createMutation.isPending}
        />
      </div>
    );
  }

  // Desktop: split-pane view
  return (
    <div className="h-[calc(100dvh-3.5rem)]">
      <ResizablePanelGroup orientation="horizontal">
        {/* Left: Ticket List */}
        <ResizablePanel defaultSize={35} minSize={25}>
          <ListPanel
            tickets={filteredTickets}
            isLoading={isLoading}
            selectedId={selectedId}
            search={search}
            onSearchChange={setSearch}
            onSelect={(id) => navigate(`/tickets/${id}${normalizedSearch}`)}
            onCreateClick={() => setDialogOpen(true)}
          />
        </ResizablePanel>

        <ResizableHandle withHandle />

        {/* Right: Detail */}
        <ResizablePanel defaultSize={65} minSize={10}>
          {selectedId ? (
            <TicketDetailPanel ticketId={selectedId} />
          ) : (
            <div className="flex flex-col items-center justify-center h-full text-muted-foreground">
              <Ticket className="h-12 w-12 mb-3 opacity-20" />
              <p className="text-sm">Select a ticket to view details</p>
            </div>
          )}
        </ResizablePanel>
      </ResizablePanelGroup>

      <CreateDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        onSubmit={(data) => createMutation.mutate(data)}
        isPending={createMutation.isPending}
      />
    </div>
  );
}

// ─── List Panel ──────────────────────────────────────────────

interface ListPanelProps {
  tickets: TicketType[];
  isLoading: boolean;
  selectedId?: string;
  search: string;
  onSearchChange: (val: string) => void;
  onSelect: (id: string) => void;
  onCreateClick: () => void;
}

function ListPanel({
  tickets,
  isLoading,
  selectedId,
  search,
  onSearchChange,
  onSelect,
  onCreateClick,
}: ListPanelProps) {
  return (
    <div className="flex flex-col h-full border-r">
      {/* Search + Create */}
      <div className="flex items-center gap-2 p-3 border-b">
        <Input
          placeholder="Search tickets..."
          value={search}
          onChange={(e) => onSearchChange(e.target.value)}
          className="h-8 text-sm"
        />
        <Button
          size="icon"
          variant="outline"
          className="h-8 w-8 shrink-0"
          onClick={onCreateClick}
        >
          <Plus className="h-4 w-4" />
        </Button>
      </div>

      {/* Ticket list */}
      <ScrollArea className="flex-1">
        {isLoading ? (
          <div className="p-4 space-y-4">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="space-y-2">
                <div className="h-3 w-20 rounded bg-muted animate-pulse" />
                <div className="h-4 w-full rounded bg-muted animate-pulse" />
                <div className="h-3 w-32 rounded bg-muted animate-pulse" />
              </div>
            ))}
          </div>
        ) : tickets.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
            <Ticket className="h-8 w-8 mb-2 opacity-20" />
            <p className="text-sm">No tickets found</p>
          </div>
        ) : (
          tickets.map((ticket) => (
            <TicketListItem
              key={ticket.id}
              ticket={ticket}
              isSelected={ticket.id === selectedId}
              onClick={() => onSelect(ticket.id)}
            />
          ))
        )}
      </ScrollArea>
    </div>
  );
}

// ─── Create Dialog ───────────────────────────────────────────

interface CreateDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (data: TicketFormValues) => void;
  isPending: boolean;
}

function CreateDialog({
  open,
  onOpenChange,
  onSubmit,
  isPending,
}: CreateDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Create Ticket</DialogTitle>
          <DialogDescription>
            Fill in the details to create a new support ticket.
          </DialogDescription>
        </DialogHeader>
        <TicketForm onSubmit={onSubmit} isPending={isPending} />
      </DialogContent>
    </Dialog>
  );
}
